import React from "react";
import { Navigate, useLocation, useNavigate } from "react-router";
import { useMutation, useQuery } from "@tanstack/react-query";
import { getQueryClient } from "../../providers/QueryProvider";
import { useAuth } from "../../contexts/AuthContext";
import { getApiClient } from "../../api/api-client";
import { API_ENDPOINTS } from "../../api/endpoints";
import type { PrivacyPolicySyncState } from "../../api/types/auth";
import Button from "../../components/ui/Button";
import SafeHtmlContent from "../../components/ui/SafeHtmlContent";
import { queryKeys } from "../../api/queryKeys";
import { privacyPolicyQueries } from "../../api/queries/privacyPolicy";

type LocationState = {
  nextPath?: string;
};

type PrivacyPolicyAcceptanceResult = {
  policy: {
    id?: string;
    version: number;
    title: string;
    summary?: string | null;
    content: string;
  };
  user: {
    id: string;
    acceptedPrivacyPolicyVersion: number;
  };
  requiresAcceptance: boolean;
};

const PrivacyPolicyAcceptance: React.FC = () => {
  const { user, isAuthenticated, isLoading, refresh } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const nextPath = (location.state as LocationState | null | undefined)
    ?.nextPath;

  const policyQuery = useQuery({
    ...privacyPolicyQueries.sync(user?.id),
    enabled: !!user && isAuthenticated,
  });

  const acceptMutation = useMutation({
    mutationFn: () =>
      getApiClient()
        .getHttpClient()
        .post<PrivacyPolicyAcceptanceResult>(
          API_ENDPOINTS.PRIVACY_POLICY.ACCEPT,
        ),
    onSuccess: async (response) => {
      // The accepted version lives on the session user; re-read it from the server.
      await refresh();

      await getQueryClient().invalidateQueries({
        queryKey: queryKeys.privacyPolicySync(user?.id),
      });
      getQueryClient().setQueryData<PrivacyPolicySyncState>(
        queryKeys.privacyPolicySync(user?.id),
        (current) =>
          current
            ? {
                ...current,
                acceptedVersion: response.user.acceptedPrivacyPolicyVersion,
                requiresAcceptance: false,
              }
            : {
                policy: response.policy,
                acceptedVersion: response.user.acceptedPrivacyPolicyVersion,
                requiresAcceptance: false,
              },
      );

      navigate(
        nextPath ||
          (user?.role === "ADMIN"
            ? "/admin/platform-overview"
            : "/teacher/overview"),
        { replace: true },
      );
    },
  });

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-slate-600">Loading...</div>
      </div>
    );
  }

  if (!isAuthenticated || !user) {
    return <Navigate to="/auth/login" replace />;
  }

  if (
    !policyQuery.isLoading &&
    policyQuery.data &&
    !policyQuery.data.requiresAcceptance
  ) {
    return (
      <Navigate
        to={
          nextPath ||
          (user.role === "ADMIN"
            ? "/admin/platform-overview"
            : "/teacher/overview")
        }
        replace
      />
    );
  }

  const policy = policyQuery.data?.policy;
  const summary = policy?.summary || "Please review the updated policy below.";

  return (
    <div className="min-h-screen bg-linear-to-br from-slate-50 via-white to-cyan-50 px-4 py-10">
      <div className="mx-auto flex w-full max-w-4xl flex-col gap-6 rounded-3xl border border-slate-200 bg-white p-6 shadow-xl md:p-10">
        <div className="space-y-2">
          <p className="text-sm font-semibold uppercase tracking-[0.2em] text-cyan-700">
            Privacy Policy Update
          </p>
          <h1 className="text-3xl font-bold text-slate-900">
            Please review and accept the latest privacy policy
          </h1>
          <p className="text-slate-600">
            Your account needs one quick confirmation before you can continue.
          </p>
        </div>

        {policyQuery.isLoading ? (
          <div className="rounded-2xl border border-slate-200 bg-slate-50 p-6 text-slate-600">
            Loading the latest policy...
          </div>
        ) : policy ? (
          <div className="space-y-4 rounded-2xl border border-slate-200 bg-slate-50 p-6">
            <div className="flex flex-col gap-1">
              <h2 className="text-xl font-semibold text-slate-900">
                {policy.title}
              </h2>
              <p className="text-sm text-slate-500">Version {policy.version}</p>
              <p className="text-slate-700">{summary}</p>
            </div>
            <div className="max-h-112 overflow-auto rounded-xl border border-slate-200 bg-white p-6">
              <SafeHtmlContent html={policy.content} className="text-sm" />
            </div>
          </div>
        ) : (
          <div className="rounded-2xl border border-amber-200 bg-amber-50 p-6 text-amber-900">
            No published privacy policy is available right now.
          </div>
        )}

        {policyQuery.error ? (
          <div className="rounded-2xl border border-red-200 bg-red-50 p-4 text-red-700">
            We could not load the latest privacy policy. Please try again.
          </div>
        ) : null}

        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-sm text-slate-500">
            By continuing, you acknowledge the latest privacy policy for your
            account.
          </p>
          <Button
            type="button"
            onClick={() => acceptMutation.mutate()}
            disabled={acceptMutation.isPending || !policy}
            className="min-w-44"
          >
            {acceptMutation.isPending ? "Accepting..." : "Accept policy"}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicyAcceptance;
